// Local `EventCard` — the consumer-search replacement for the MISSING
// `@eventbrite/event-renderer` `EventCard` (legacy recipe component, no
// sanctioned equivalent; `EventCard` is on the do-not-import list). It renders
// the SAME vertical card the legacy carousels consumed, built from the
// sanctioned `@eventbrite/consumer-search.event-renderer` primitives
// (VerticalEventCard + EventCardImage + UTM/aff-tagged EventCardLink +
// name/date/venue/price + save/share actions) — matching the legacy
// `EventCard` API the carousels call (eventData=FormattedEvent, affCode,
// isAuthenticated, shareOptions, description, onDidShareClick/onWillShareClick,
// gaCategory).
//
// The legacy recipe it replaces is eb-ui
// `event-renderer/src/recipes/VerticalEventCard/VerticalFullClickEventCard.tsx`
// (full-click link + raised-card look), adapted: the legacy `EventCard`
// wrapper's `onDidShareClick`/`onWillShareClick` callbacks (the consumer's
// share tracking seam) are wired into the sanctioned `EventCardShareAction`
// via `getShareActionProps`' `onClick`/`onShareItemClick`.
import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardShareAction,
    EventCardTitle,
    EventCardTracking,
    VerticalEventCard,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    getShareActionProps,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    type FormattedEvent,
} from '../eventRenderer';
import React from 'react';

import styles from './EventCard.module.scss';

// Missing from the sanctioned package (do-not-import): the vertical image
// config — the literal values from the eb-ui original
// (`event-renderer/src/components/EventCardImage/constants.ts`).
const VERTICAL_EVENT_CARD_IMAGE_CONFIG = {
    '--image-aspect-ratio': '2',
    '--image-aspect-ratio-mobile': '2',
    '--image-width': '100%',
    '--image-width-mobile': '100%',
};

interface ShareOptionsLike {
    utmOptions?: Record<string, string>;
    facebookOptions?: {
        appId: string;
        version: string;
        xfbml: boolean;
        page?: string;
        locale?: string;
    };
}

export interface EventCardProps {
    /** Preformatted event (destination-upgraded `FormattedEvent`). */
    event: FormattedEvent;
    locale?: string;
    isAuthenticated?: boolean;
    /** Affiliate code appended to the card link + share intents. */
    affCode?: string;
    /** GA category the legacy `EventCard` tracked clicks into (dropped GA). */
    gaCategory?: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    /** Share options (the app's `share_props`-derived event share shape). */
    shareOptions?: ShareOptionsLike;
    onDidShareClick?: (shareType: string) => void;
    onWillShareClick?: () => void;
    /** Repeating-events note (legacy `EventCard` `description`). */
    description?: React.ReactNode;
    isLazyImage?: boolean;
    isRaised?: boolean;
    isTargetBlank?: boolean;
    shouldConvertToUserTimezone?: boolean;
    statsigLocationString?: string;
    /** The `_shared` signal prop (organizer info hidden for own-org events). */
    signal?: undefined;
    [key: string]: unknown;
}

export const EventCard: React.FC<EventCardProps> = (props) => {
    const {
        event,
        locale: localeProp,
        isAuthenticated,
        affCode,
        onClick,
        shareOptions,
        onDidShareClick,
        onWillShareClick,
        description,
        isLazyImage,
        isRaised,
        isTargetBlank,
        statsigLocationString,
    } = props;
    const locale = localeProp || 'en_US';

    const linkProps = getEventCardLinkProps({
        affCode: affCode || '',
        event,
        onClick,
        isTargetBlank,
        statsigLocationString,
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedSalesStatus = getFormattedSalesStatus(event);

    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    // Share action — only when the consumer supplied share options (the
    // legacy `EventCard` gate: `shareOptions` present ⇒ render the share icon).
    // The sanctioned `getShareActionProps` `facebookOptions` marks `page`/
    // `locale` as the empty-string literal (`''`), so only the `appId`/
    // `version`/`xfbml` (real values) are passed; `page`/`locale` fall back to
    // the sanctioned action's defaults (`''`/`en_US`).
    const moreActions = shareOptions ? (
        <EventCardShareAction
            {...getShareActionProps({
                name: event.name,
                id: event.id,
                url: event.url,
                affCode: affCode || '',
                utmOptions: shareOptions.utmOptions,
                facebookOptions: shareOptions.facebookOptions
                    ? {
                          appId: shareOptions.facebookOptions.appId,
                          version: shareOptions.facebookOptions.version,
                          xfbml: shareOptions.facebookOptions.xfbml,
                      }
                    : undefined,
                onClick: () => {
                    onWillShareClick?.();
                },
                onShareItemClick: (shareType: string) => {
                    onDidShareClick?.(shareType);
                },
            })}
        />
    ) : undefined;

    return (
        <VerticalEventCard isRaised={isRaised} fullClick>
            <EventCardTracking
                event={event}
                statsigLocationString={statsigLocationString}
            >
                <EventCardLink
                    {...linkProps}
                    className={`${styles.card} eds-event-card--consumer`}
                >
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>
                    <EventCardImage
                        name={event.name}
                        loading={isLazyImage ? 'lazy' : 'eager'}
                        tokens={{
                            ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                            '--image-background-color': event.edgeColor,
                            '--image-object-position': event.focalPoint
                                ? `${event.focalPoint.x * 100}% ${event.focalPoint.y * 100}%`
                                : undefined,
                        }}
                        src={event.imageUrl}
                        imageSizes={event.imageSizes}
                        preferredSize="medium"
                    />
                    <EventCardDetails
                        space="xxs"
                        tokens={{
                            '--EventCardDetailsPadding': '16px 8px 12px 12px',
                            '--EventCardDetailsFlexGrow': '2',
                            '--EventCardDetailsPosition': 'relative',
                        }}
                    >
                        <EventCardPromoted isPromoted={event.isPromoted} />
                        <EventCardTitle
                            className="event-card__clamp-line--two"
                            title={event.name}
                            color="neutral-800"
                        />
                        <EventCardDate
                            className="event-card__clamp-line--one"
                            formattedDateString={formattedDateString}
                            variant="body-md"
                        />
                        <EventCardLocation
                            className="event-card__clamp-line--one"
                            formattedLocationString={formattedLocationString}
                        />
                        <EventCardPrice
                            color="neutral-800"
                            isFree={event.isFree}
                            formattedPriceString={formattedPriceString}
                            variant="body-md-bold"
                        />
                        {description}
                    </EventCardDetails>
                </EventCardLink>

                <EventCardActions
                    tokens={{
                        '--EventCardActionsPosition': 'absolute',
                        '--EventCardActionsTop': '-22px',
                        '--EventCardActionsRight': '8px',
                        '--EventCardActionsGap': '8px',
                    }}
                    actionStyleOverride="outline"
                >
                    <EventCardSaveAction
                        isAuthenticated={!!isAuthenticated}
                        savedByYou={event.savedByYou}
                        id={event.id}
                        eventName={event.name}
                        imageUrl={event.imageUrl}
                    />
                    {moreActions}
                </EventCardActions>
            </EventCardTracking>
        </VerticalEventCard>
    );
};

export default EventCard;
